import { useState } from "react";
import { toast } from "react-toastify";
import { confirmPayment, fetchOrderDetails } from "../services/useService";
import { OrderDetail, Payment } from "../utils/types";

export const usePayment = (order: OrderDetail | null, setOrder: (order: OrderDetail) => void, setStatus: (status: string) => void, orderid?: string) => {
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [modals, setModals] = useState({ cash: false, credit: false, print: false });
  const [receivedPrice, setReceivedPrice] = useState<number>(0);
  const [latestPayment, setLatestPayment] = useState({
    ReceivedAmount: 0,
    Paymentmode: "",
    newReceivedAmount: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const { cash: openCashModal, credit: openCreditModal, print: showPrintContent } = modals;

  // Calculate total received amount from payment history
  const calculateTotalReceived = (orderData: OrderDetail | null): number => {
    if (!orderData) return 0;
    
    // If paymentHistory exists, sum all ReceivedAmount from paymentHistory
    if (orderData.paymentHistory && orderData.paymentHistory.length > 0) {
      return orderData.paymentHistory.reduce(
        (sum: number, item: Payment) => sum + Number(item?.ReceivedAmount || 0),
        0
      );
    }
    
    // Fallback to payments array
    return Number(orderData.payments?.[0]?.ReceivedAmount || 0);
  };

  const handleConfirmPayment = (Payment: string) => {
    if (!order) return;
    if (!orderid) {
      toast.error("Order ID is missing");
      return;
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    const previousTotalReceived = calculateTotalReceived(order);
    const TOTALAMOUNT = Number(order?.totalOrderPrice || order?.totalAmount || 0);

    // Calculate new total received amount
    const newTotalReceived = previousTotalReceived + receivedPrice;
    const RemainingAmount = TOTALAMOUNT - newTotalReceived;

    // Create new payment history entry
    const newPaymentEntry: Payment & { PaymentDate?: string; PaymentTime?: string } = {
      ReceivedAmount: receivedPrice,
      Paymentmode: Payment,
      PaymentDate: `${year}-${month}-${day}`,
      PaymentTime: `${hours}:${minutes}:${seconds}`
    };

    // Prepare updated payment history - include existing payments and add new one
    const existingPaymentHistory: Payment[] = order.paymentHistory || [];
    
    // If no existing payment history but we have payments data, convert it to history
    if (existingPaymentHistory.length === 0 && order.payments && order.payments.length > 0) {
      const initialPayment = order.payments[0];
      if (initialPayment && initialPayment.ReceivedAmount > 0) {
        existingPaymentHistory.push({
          ReceivedAmount: initialPayment.ReceivedAmount,
          Paymentmode: initialPayment.Paymentmode,
          PaymentDate: order.Orderdate,
          PaymentTime: order.Ordertime
        } as Payment & { PaymentDate?: string; PaymentTime?: string });
      }
    }

    const updatedPaymentHistory = [
      ...existingPaymentHistory,
      newPaymentEntry
    ];

    const updatePayload = {
      Finalamount: TOTALAMOUNT,
      RemainingAmount,
      OrderStatus: RemainingAmount <= 0 ? "Delivered" : order.OrderStatus,
      // DeliveryDate: RemainingAmount <= 0 ? `${year}-${month}-${day}` : order.DeliveryDate || order.Orderdate,
      // DeliveryTime: RemainingAmount <= 0 ? `${hours}:${minutes}:${seconds}` : order.DeliveryTime || order.Ordertime,
      Paymentmode: Payment,
      ReceivedAmount: newTotalReceived,
      id: order.Customerid,
      paymentHistory: updatedPaymentHistory,
      payments: [
        {
          ...(order.payments?.[0] || {}),
          ReceivedAmount: newTotalReceived,
          Paymentmode: Payment,
        },
      ],
    };

    confirmPayment(orderid, updatePayload)
      .then((res) => {
        if (RemainingAmount <= 0) {
          setStatus("Delivered");
        }

        const updatedOrder = {
          ...res.data.result,
          RemainingAmount,
          payments: [
            {
              ...(order.payments?.[0] || {}),
              ReceivedAmount: newTotalReceived,
              Paymentmode: Payment,
            },
          ],
          paymentHistory: updatedPaymentHistory
        };

        setOrder(updatedOrder);

        setLatestPayment({
          ReceivedAmount: receivedPrice,
          Paymentmode: Payment,
          newReceivedAmount: newTotalReceived,
        });

        toast.success("Payment completed successfully", {
          position: "top-right",
          autoClose: 1500,
        });
        
        return fetchOrderDetails(orderid);
      })
      .then((res) => {
        const serverOrder = res.data.result;
        console.log("Updated Order from Server:", serverOrder);
        console.log("Payment History from Server:", serverOrder.paymentHistory);
        setOrder(serverOrder);
        
        setModals((prev) => ({ ...prev, print: true }));
        setTimeout(() => {
          window.print();
          setModals((prev) => ({ ...prev, print: false }));
        }, 500);
      })
      .catch((error) => {
        console.error("Payment error:", error);
        setError("Failed to update the order");
        toast.error("Failed to complete payment", {
          position: "top-right",
          autoClose: 3000,
        });
      });

    setModals({ cash: false, credit: false, print: false });
  };

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedMethod = event.target.value;
    setPaymentMethod(selectedMethod);
    if (selectedMethod === "Cash") {
      setModals((prev) => ({ ...prev, cash: true }));
    } else if (selectedMethod === "Card") {
      setReceivedPrice(Number(order?.RemainingAmount || 0));
      setModals((prev) => ({ ...prev, credit: true }));
    }
  };

  const handleCloseModal = () => {
    setModals({ cash: false, credit: false, print: false });
    setPaymentMethod("");
    setReceivedPrice(0);
  };

  return {
    paymentMethod,
    openCashModal,
    openCreditModal,
    receivedPrice,
    setReceivedPrice,
    handleConfirmPayment,
    handlePaymentMethodChange,
    handleCloseModal,
    latestPayment,
    showPrintContent,
    setModals,
    error,
    calculateTotalReceived,
  };
};